import { useCallback } from 'react';
import {
  ApiResult,
  useService,
  useAuthenticatedSWR,
  wrapErrorWithResult,
} from '../api';
import { SUBSCRIPTIONS_V4_BASE_URL } from '../subscriptions';
import {
  MX_EXTRAS_TREAT_PRODUCT_CODE,
  MX_EXTRAS_SUPPLEMENT_PRODUCT_CODE,
  MX_EXTRAS_DENTAL_CHEW_PRODUCT_CODE,
} from './constants';
import { ExtrasService } from './service';

export const useExtras = subscriptionId => {
  const service = useService(ExtrasService);

  const { data, mutate, error, isValidating } = useAuthenticatedSWR(
    subscriptionId
      ? `${SUBSCRIPTIONS_V4_BASE_URL}${subscriptionId}/extras/`
      : null,
  );

  const extras = {};

  if (Boolean(data?.length)) {
    data.forEach(extra => {
      const isDentalChew =
        extra.product_code === MX_EXTRAS_DENTAL_CHEW_PRODUCT_CODE;
      const productCode = extra.product_code;
      const isRecommended = extra.is_recommended;
      if (!extras[productCode]) {
        extras[productCode] = [];
      }

      if (!isDentalChew) extras[productCode].push(extra);
      if (isDentalChew && isRecommended) extras[productCode].push(extra);
    });
  }

  const updateSubscriptionExtras = useCallback(
    async extras => {
      try {
        const response = await service.updateSubscriptionExtras(
          subscriptionId,
          extras,
        );
        return ApiResult.success(response);
      } catch (error) {
        return wrapErrorWithResult(error);
      }
    },
    [service, subscriptionId],
  );

  const deleteSubscriptionExtra = useCallback(
    async extraId => {
      try {
        const response = await service.deleteSubscriptionExtra(
          subscriptionId,
          extraId,
        );
        return ApiResult.success(response);
      } catch (error) {
        return wrapErrorWithResult(error);
      }
    },
    [service, subscriptionId],
  );

  const revalidate = useCallback(() => mutate(), [mutate]);

  return {
    isLoading: subscriptionId ? !data && !error : false,
    error,
    treatsData: extras[MX_EXTRAS_TREAT_PRODUCT_CODE],
    supplementsData: extras[MX_EXTRAS_SUPPLEMENT_PRODUCT_CODE],
    dentalChewsData: extras[MX_EXTRAS_DENTAL_CHEW_PRODUCT_CODE],
    updateSubscriptionExtras,
    deleteSubscriptionExtra,
    revalidate,
    isValidating,
  };
};
